import React, { useCallback, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useMatch } from 'react-router-dom';

import { SpinnerOverlay } from '@components/common';
import ErrorPage from '@components/common/ErrorPage';
import SideBar from '@components/common/SideBar';

import { useRedirectOnOrgSwitch } from '@hooks/routes/useRedirectOnOrgSwitch';
import { useRedirectWhenEmptyMenu } from '@hooks/routes/useRedirectWhenEmptyMenu';

import { isEmpty } from '@utils/common';
import { decideDirection } from '@utils/decideDirection';
import { pickLongestMatchedRoute, sliceToLocalPath } from '@utils/routes/routesUtils';

import { IMenuItem } from '@components/common/SideBar/interfaces';
import { IRouteItem } from './interfaces';

import { CLOSE_WIDTH_SIDEBAR, FULL_WIDTH_SIDEBAR } from '@constants/common';
import { EErrorPageCodeKey } from '@components/common/ErrorPage/constants';

import styles from './RoutesWrapper.module.scss';

interface IRoutesWrapperProps {
	sideBarTitle: string;
	sideBarItems: IMenuItem[];
	routesList: IRouteItem[];
	firstNotHiddenItem?: string;
	isLoading: boolean;
	basePathPrefix?: string;
}

const RoutesWrapper = ({
	sideBarTitle,
	routesList,
	firstNotHiddenItem,
	sideBarItems,
	isLoading,
	basePathPrefix,
}: IRoutesWrapperProps) => {
	const [isFullSizeSideBar, setIsFullSizeSideBar] = useState(false);
	const [showIsVisibleSideBar, setShowIsVisibleSiteBar] = useState(true);

	const hideSideBar = useCallback(() => setShowIsVisibleSiteBar(false), []);
	const { pathname } = useLocation();

	const contentMaxWidthStyle = useMemo(() => ({
		maxWidth: isFullSizeSideBar
			? `calc(100% - ${FULL_WIDTH_SIDEBAR})`
			: `calc(100% - ${CLOSE_WIDTH_SIDEBAR})`,
	}), [isFullSizeSideBar]);

	const matchedBasePath = useMatch(`${basePathPrefix ?? ''}/*`);

	const localPathname = sliceToLocalPath({
		pathname,
		basePathPrefix,
		pathnameBase: matchedBasePath?.pathnameBase,
});

	const matchedRouteItem = useMemo(
		() => pickLongestMatchedRoute({ routesList, localPathname }),
		[routesList, localPathname]);

	const isAllowedHereInsideAdmin = matchedRouteItem?.condition ?? true;
	const isAdminRoutesReady = !isLoading && routesList.length > 0 && Boolean(firstNotHiddenItem);
	const isBaseMatchReady = !basePathPrefix || !!matchedBasePath;

	useRedirectOnOrgSwitch({
		isReady: true,
		isAllowedHere: isAllowedHereInsideAdmin,
		isGateReady: isAdminRoutesReady && isBaseMatchReady,
		isConsumeFlagWhenAllowed: true,
	});

	useRedirectWhenEmptyMenu({
		routesCount: routesList.length,
		sideBarItemsCount: sideBarItems.length,
		isLoading,
		to: '/',
	});

	return (
		<div className={styles.panelContainer}>
			{showIsVisibleSideBar
				&& <SideBar
					setIsFullSizeSideBar={setIsFullSizeSideBar}
					sideBarItems={sideBarItems}
					title={sideBarTitle}
				/>
			}
			<div
				className={styles.panelContent}
				style={showIsVisibleSideBar ? contentMaxWidthStyle : { maxWidth: '100%' }}
			>
				{isEmpty(routesList) || !firstNotHiddenItem
					? <SpinnerOverlay />
					: <Routes>
						<Route
							element={decideDirection({
								condition: false,
								Component: null,
								pathTo: firstNotHiddenItem,
							})}
							path='/'
						/>
						<Route
							element={<ErrorPage
								errorKey={EErrorPageCodeKey.notFound}
								hideBar={hideSideBar}
							/>}
							path='*'
						/>
						{routesList.map((item: any, index: number) => {
							const { condition, Component, path } = item;

							return (<Route
								key={`route-key-index-${sideBarTitle}-${index}`}
								element={condition
									? <Component />
									: <ErrorPage
										hideBar={hideSideBar}
									/>}
								path={path}
							/>);
						})}
					</Routes>
				}
			</div>
		</div>
	);
};

export default RoutesWrapper;
